import React from 'react';
import { Camera, Zap, Plus, RefreshCw, Search, Radar } from 'lucide-react';
import { Button } from '../ui/button';

const DiscoveryTab = ({
  scanResults,
  isScanning,
  startNetworkScan,
  registerDiscoveredDevice,
}) => {
  return (
    <div className="space-y-6">
      {/* Scan Controls */}
      <div className="bg-white rounded-xl border shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-semibold text-gray-900 flex items-center gap-2">
              <Radar className="h-5 w-5 text-blue-500" />
              Network Auto-Discovery
            </h3>
            <p className="text-sm text-gray-600">Scan your network to automatically discover IoT devices</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => startNetworkScan('quick')}
              disabled={isScanning}
            >
              {isScanning ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              <span className="ml-2">Quick Scan</span>
            </Button>
            <Button
              onClick={() => startNetworkScan('deep')}
              disabled={isScanning}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Radar className="h-4 w-4 mr-2" />
              Deep Scan
            </Button>
          </div>
        </div>

        {isScanning && (
          <div className="p-4 bg-blue-50 rounded-lg flex items-center gap-3">
            <RefreshCw className="h-5 w-5 text-blue-600 animate-spin" />
            <span className="text-blue-700">Scanning network for IoT devices...</span>
          </div>
        )}
      </div>

      {/* Scan Results */}
      {scanResults && (
        <div className="bg-white rounded-xl border shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">
              Discovered Devices ({scanResults.devices_found})
            </h3>
            <div className="flex gap-2 text-sm">
              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                {scanResults.summary?.cameras || 0} Cameras
              </span>
              <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded">
                {scanResults.summary?.smart_meters || 0} Meters
              </span>
              <span className="px-2 py-1 bg-red-100 text-red-700 rounded">
                {scanResults.summary?.with_security_issues || 0} With Issues
              </span>
            </div>
          </div>

          <div className="space-y-3">
            {scanResults.devices?.map((device) => (
              <div key={device.id} className="p-4 border rounded-lg hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg ${device.category === 'cameras' ? 'bg-blue-100' : 'bg-yellow-100'}`}>
                      {device.category === 'cameras' ?
                        <Camera className="h-5 w-5 text-blue-600" /> :
                        <Zap className="h-5 w-5 text-yellow-600" />
                      }
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{device.manufacturer} {device.model}</p>
                      <p className="text-sm text-gray-600">{device.ip_address} &bull; {device.mac_address}</p>
                      <div className="flex gap-2 mt-1">
                        {device.open_ports?.map(port => (
                          <span key={port} className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                            Port {port}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    {device.security_issues?.length > 0 ? (
                      <div className="mb-2">
                        {device.security_issues.map((issue, idx) => (
                          <span key={`${device.id}-issue-${idx}`} className={`text-xs px-2 py-0.5 rounded mr-1 ${
                            issue.severity === 'critical' ? 'bg-red-100 text-red-700' :
                            issue.severity === 'high' ? 'bg-orange-100 text-orange-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {issue.type.replace('_', ' ')}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700 mb-2 inline-block">
                        No issues
                      </span>
                    )}
                    <Button
                      size="sm"
                      onClick={() => {
                        const name = prompt('Enter device name:', `${device.manufacturer} ${device.device_type}`);
                        const location = prompt('Enter location:', 'Main Building');
                        if (name && location) {
                          registerDiscoveredDevice(scanResults.scan_id, device.ip_address, name, location);
                        }
                      }}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Register
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!scanResults && !isScanning && (
        <div className="bg-white rounded-xl border shadow-sm p-12 text-center">
          <Radar className="h-16 w-16 mx-auto mb-4 text-gray-300" />
          <h4 className="font-medium text-gray-700 mb-2">No scan results yet</h4>
          <p className="text-sm text-gray-500 mb-4">Run a network scan to discover IoT devices automatically</p>
        </div>
      )}
    </div>
  );
};

export default DiscoveryTab;
